import React from "react";
import { Typography } from "antd";
import { WA } from "../../theme";
import TextoWhatsapp from "../Chat/TextoWhatsapp";

const { Text } = Typography;

// Categorías que ofrecemos al crear plantillas
export const CATEGORIAS = [
  { value: "MARKETING", label: "Marketing" },
  { value: "UTILITY", label: "Utilidad" },
];

// Idiomas disponibles para Meta.
export const IDIOMAS = [
  { value: "es_MX", label: "Español (México)" },
  { value: "es", label: "Español" },
  { value: "en_US", label: "Inglés (EE. UU.)" },
  { value: "en", label: "Inglés" },
];

// Modelos del sistema a los que se puede ligar una plantilla.
export const MODELOS = [
  { value: "pedido", label: "Pedido" },
  { value: "cotizacion", label: "Cotización" },
  { value: "venta", label: "Venta" },
  { value: "ordencompra", label: "Orden de Compra" },
  { value: "cliente", label: "Cliente" },
  { value: "proveedor", label: "Proveedor" },
  { value: "insumo", label: "Insumo" },
  { value: "lead", label: "Lead" },
  { value: "contacto", label: "Contacto" },
];

export const etiquetaModelo = (m) => MODELOS.find((x) => x.value === m)?.label || m;

// Variables sugeridas por modelo 
const DOC = [
  "serie_folio", "total", "subtotal", "impuestos_total", "cliente",
  "cantidad_productos", "observaciones", "rfc", "fecha_documento", "fecha_actual",
];
export const VARIABLES_SUGERIDAS = {
  pedido: DOC,
  cotizacion: DOC,
  venta: DOC,
  ordencompra: ["serie_folio", "total", "subtotal", "impuestos_total", "proveedor", "cantidad_productos", "observaciones", "rfc", "fecha_documento", "fecha_actual"],
  cliente: ["rfc", "nombre", "tipo_cliente", "regimen_fiscal", "direccion_facturacion", "correo", "telefono"],
  proveedor: ["rfc", "nombre", "regimen_fiscal", "tipo_proveedor", "correo", "telefono"],
  insumo: ["descripcion", "descripcion_larga", "clave", "codigo", "codigo_barras", "marca", "tipo", "familia", "unidad", "precios"],
  lead: ["descripcion", "numero", "correo", "valor", "origen", "nombre_contacto", "nombre_embudo", "nombre_etapa"],
  contacto: ["nombre", "correo", "numero"],
};


export const selectorDeModelo = (modelo) => {
  if (["pedido", "cotizacion", "venta", "ordencompra"].includes(modelo)) {
    const tipo = { pedido: "Pedido", cotizacion: "Cotizacion", venta: "Venta", ordencompra: "OrdenDeCompra" }[modelo];
    return { tipo: "documento", documentoTipo: tipo };
  }
  if (modelo === "cliente") return { tipo: "cliente" };
  if (modelo === "proveedor") return { tipo: "proveedor" };
  if (modelo === "insumo") return { tipo: "insumo" };
  if (modelo === "lead") return { tipo: "lead" };
  if (modelo === "contacto") return { tipo: "contacto" };
  return null;
};

// Variables CON NOMBRE: {{cliente}}, {{serie_folio}}, ...
const RE_VAR = /\{\{([a-zA-Z0-9_]+)\}\}/g;

// Devuelve los nombres de variable únicos (en orden de aparición).
export const variablesDe = (texto) => {
  if (!texto) return [];
  const out = [];
  let m;
  RE_VAR.lastIndex = 0;
  while ((m = RE_VAR.exec(texto))) {
    if (!out.includes(m[1])) out.push(m[1]);
  }
  return out;
};

// Sustituye {{nombre}} por valores[nombre] (para el preview).
export const rellenar = (texto, valores = {}) =>
  String(texto || "").replace(RE_VAR, (_, n) => (valores[n] != null && valores[n] !== "" ? valores[n] : `{{${n}}}`));

export const analizarPlantilla = (plantilla) => {
  const comps = plantilla?.components || [];
  const header = comps.find((c) => c.type === "HEADER") || null;
  const body = comps.find((c) => c.type === "BODY") || null;
  const footer = comps.find((c) => c.type === "FOOTER") || null;
  const buttonsComp = comps.find((c) => c.type === "BUTTONS") || null;
  return {
    header,
    bodyTexto: body?.text || "",
    footerTexto: footer?.text || "",
    botones: buttonsComp?.buttons || [],
    esHeaderMedia: !!header && ["IMAGE", "VIDEO", "DOCUMENT"].includes(header.format),
    esHeaderTexto: !!header && header.format === "TEXT",
    varsHeader: header?.format === "TEXT" ? variablesDe(header.text) : [],
    varsBody: variablesDe(body?.text),
  };
};

export const botonesUrlDinamicos = (analisis) =>
  (analisis.botones || [])
    .map((b, index) => ({ index, b }))
    .filter(({ b }) => b.type === "URL" && /\{\{.+?\}\}/.test(b.url || ""));

export const construirComponentsEnvio = (analisis, valoresHeader = {}, valoresBody = {}, valoresBoton = {}) => {
  const components = [];
  if (analisis.esHeaderTexto && analisis.varsHeader.length) {
    components.push({
      type: "header",
      parameters: analisis.varsHeader.map((n) => ({ type: "text", parameter_name: n, text: valoresHeader[n] || "" })),
    });
  }
  if (analisis.varsBody.length) {
    components.push({
      type: "body",
      parameters: analisis.varsBody.map((n) => ({ type: "text", parameter_name: n, text: valoresBody[n] || "" })),
    });
  }
  botonesUrlDinamicos(analisis).forEach(({ index }) => {
    components.push({
      type: "button",
      sub_type: "url",
      index: String(index),
      parameters: [{ type: "text", text: valoresBoton[index] || "" }],
    });
  });
  return components;
};

export const etiquetaEstado = (status) => {
  switch (status) {
    case "APPROVED": return "Aprobada";
    case "PENDING": return "Pendiente";
    case "REJECTED": return "Rechazada";
    case "PAUSED": return "Pausada";
    case "DISABLED": return "Deshabilitada";
    default: return status || "—";
  }
};

export const colorEstado = (status) => {
  switch (status) {
    case "APPROVED": return "#52c41a";
    case "PENDING": return "#faad14";
    case "REJECTED":
    case "DISABLED": return "#f5222d";
    default: return WA.textSecondary;
  }
};

export const motivoRechazo = (code) => {
  const M = {
    ABUSIVE_CONTENT: "Contenido abusivo o que infringe las políticas de WhatsApp.",
    INVALID_FORMAT: "Formato inválido (revisa variables, ejemplos o estructura).",
    PROMOTIONAL: "Se consideró promocional en una categoría que no lo permite.",
    TAG_CONTENT_MISMATCH: "El contenido no coincide con la categoría seleccionada.",
    SCAM: "Se detectó como posible fraude.",
    INCORRECT_CATEGORY: "La categoría no corresponde al contenido.",
    NONE: "",
  };
  return M[code] || code || "";
};

// Ejemplos automáticos por variable 
export const EJEMPLOS = {
  serie_folio: "P-123", total: "$1,000.00", subtotal: "$862.00", impuestos_total: "$138.00",
  cliente: "Juan Pérez", proveedor: "Distribuidora ACME S.A.", cantidad_productos: "3", observaciones: "Sin observaciones",
  rfc: "XAXX010101000", fecha_documento: "01/01/2025", fecha_actual: "01/01/2025",
  nombre: "Juan Pérez", tipo_cliente: "Mayorista", tipo_proveedor: "Nacional",
  regimen_fiscal: "601 - General de Ley", direccion_facturacion: "Av. Siempre Viva 123, Centro",
  correo: "correo@ejemplo.com", telefono: "5512345678",
  descripcion: "Producto de ejemplo", descripcion_larga: "Descripción larga de ejemplo",
  clave: "CLV-001", codigo: "1001", codigo_barras: "7501234567890",
  marca: "Bosch", tipo: "Refacción", familia: "Refacciones", unidad: "Pieza",
  precios: "Público: $250.00",
  numero: "5512345678", valor: "$5,000.00", origen: "Facebook",
  nombre_contacto: "Juan Pérez", nombre_embudo: "Ventas", nombre_etapa: "Negociación",
};
export const ejemploDe = (n) => EJEMPLOS[n] || "Ejemplo";

export const valoresEjemplo = (plantilla, analisis) => {
  const comps = plantilla?.components || [];
  const body = comps.find((c) => c.type === "BODY");
  const header = comps.find((c) => c.type === "HEADER");
  const guardados = {};
  (body?.example?.body_text_named_params || []).forEach((p) => { guardados[p.param_name] = p.example; });
  (header?.example?.header_text_named_params || []).forEach((p) => { guardados[p.param_name] = p.example; });
  const out = {};
  [...(analisis.varsHeader || []), ...(analisis.varsBody || [])].forEach((n) => {
    out[n] = guardados[n] || EJEMPLOS[n] || "Ejemplo";
  });
  return out;
};

const chipBoton = {
  display: "block",
  marginTop: 6,
  padding: "6px 10px",
  background: WA.surface,
  border: `1px solid ${WA.border}`,
  borderRadius: 6,
  textAlign: "center",
  color: WA.textPrimary,
  fontWeight: 600,
  fontSize: 13,
};

const etiquetaHeaderMedia = { IMAGE: "🖼️ Imagen", VIDEO: "🎬 Video", DOCUMENT: "📄 Documento" };


export const PreviewPlantilla = ({ plantilla, valores = {}, imagenUrl }) => {
  const a = analizarPlantilla(plantilla);
  return (
    <div>
      {a.esHeaderMedia && (
        imagenUrl && a.header.format === "IMAGE" ? (
          <img
            src={imagenUrl}
            alt=""
            style={{ width: "100%", maxHeight: 180, objectFit: "contain", borderRadius: 6, border: `1px solid ${WA.border}`, background: "#fff", marginBottom: 6 }}
          />
        ) : (
          <div
            style={{
              background: WA.surface,
              border: `1px dashed ${WA.border}`,
              borderRadius: 6,
              padding: "16px 10px",
              textAlign: "center",
              color: WA.textSecondary,
              fontSize: 12,
              marginBottom: 6,
            }}
          >
            {etiquetaHeaderMedia[a.header.format] || "Adjunto"}
          </div>
        )
      )}
      {a.esHeaderTexto && a.header?.text && (
        <div style={{ fontWeight: 700, marginBottom: 2 }}>
          <TextoWhatsapp texto={rellenar(a.header.text, valores)} />
        </div>
      )}
      {a.bodyTexto && (
        <div>
          <TextoWhatsapp texto={rellenar(a.bodyTexto, valores)} />
        </div>
      )}
      {a.footerTexto && (
        <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
          {a.footerTexto}
        </Text>
      )}
      {(a.botones || []).map((b, i) => {
        const esUrl = b.type === "URL";
        // Para URL dinámicas ({{1}}) mostramos la URL de ejemplo ya resuelta.
        const esDinamica = esUrl && /\{\{\d+\}\}/.test(b.url || "");
        const urlMostrada = esUrl ? (esDinamica ? (b.example?.[0] || b.url) : b.url) : null;
        return (
          <div key={i} style={chipBoton}>
            <div>{esUrl ? "🔗 " : ""}{b.text}</div>
            {esUrl && urlMostrada && (
              <a
                href={urlMostrada}
                target="_blank"
                rel="noreferrer"
                style={{ display: "block", fontSize: 11, fontWeight: 400, color: WA.turquesa, wordBreak: "break-all", marginTop: 2 }}
                onClick={(e) => e.stopPropagation()}
              >
                {urlMostrada}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};
